import { createRef, Component } from "react";
import { graphql } from "gatsby";
import { extract, EventAggregator, PrimaryButton } from "@parksandresorts/core";
import UiBkgImage from "@rnb/ui-bkg-image";
import LinkWrapper from "components/block-elements/link-wrapper/link-wrapper";
import styles from "./card-list-item-block.module.less";
import borderPatterns from "styles/theming/border-patterns.module.less";
import EventAttributeStore from "components/stores/event-attribute-store";
import { StringHelper } from "components/helpers";

const MEDIUM_BREAKPOINT = 768;

export default class CardListItemBlock extends Component {
  constructor(props) {
    super(props);
    this.title = extract(props, "title");
    this.link = extract(props, "link.slug");
    this.linkExternalUrl = extract(props, "linkExternalUrl");
    this.assetLink = extract(props, "assetLink.asset.file.url");
    this.openAssetInNewTab = extract(props, "assetLink.openInNewTab");
    this.buttonLabel = extract(props, "buttonLabel");
    this.description = extract(props, "cardDescription.cardDescription");
    this.topSectionText = extract(props, "topSectionText");
    this.topSectionImage = extract(props, "topSectionImage.file.url");
    this.image = extract(props, "image.file.url");
    this.backgroundImage = extract(props, "backgroundImage.file.url");
    this.backgroundImageSmallDevice = extract(props, "backgroundImageSmallDevice.file.url");
    this.borderPattern = extract(props, "borderPattern");
    this.componentTheme = extract(props, "componentTheme");
    this.alignRight = extract(props, "alignRight");
    this.useParallax = extract(props, "useParallax");
    this.isFoundationType = (this.link || this.linkExternalUrl || this.assetLink) && this.buttonLabel;

    this.parallaxDivider = 5;
    this.oldResult = 0;
    this.ref_wrapper = createRef();

    this.state = {
      transform: `translate3d(0rem, -50%, 0rem)`,
    };
  }

  componentDidMount() {
    this._mounted = true;
    if (this.useParallax && !this.props.blockInOverlay) {
      this.onScrollSubcription = EventAggregator.subscribe("onScroll", this.onScroll.bind(this));
      this.onResizeWindowWidthSubscription = EventAggregator.subscribe(
        "onResizeWindowWidth",
        this.windowWidthChanged.bind(this),
      );
      setTimeout(() => this.onScroll(), 100);
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this.onScrollSubcription) {
      this.onScrollSubcription.remove();
    }

    if (this.onResizeWindowWidthSubscription) {
      this.onResizeWindowWidthSubscription.remove();
    }
  }

  windowWidthChanged() {
    this.onScroll();
  }

  onScroll() {
    if (!this._mounted) {
      return;
    }

    if (EventAttributeStore.getCurrentWindowWidth() < MEDIUM_BREAKPOINT) {
      this.setState({
        transform: `translate3d(0rem, 0rem, 0rem)`,
      });
      return;
    }

    if (this.ref_wrapper && this.ref_wrapper.current) {
      const offset = this.ref_wrapper.current.clientHeight / 2;
      const rect = this.ref_wrapper.current.getBoundingClientRect();
      const offsetToMiddleOfScreen = rect.top - EventAttributeStore.getCurrentWindowHeight() / 2 + offset;
      const result = -(offsetToMiddleOfScreen / this.parallaxDivider).toFixed(0);
      //if we wanna cap parallax value to someting, do it here.
      //const resultMaxHundred = Math.min(100, Math.max(0, result));
      if (result !== this.oldResult) {
        this.setState({
          transform: `translate3d(${0}px, ${-offset + result}px, ${0}px)`,
        });
      }
      this.oldResult = result;
    }
  }

  getLinkUrl() {
    if (this.link) {
      return this.link;
    }
    if (this.linkExternalUrl) {
      return this.linkExternalUrl;
    }
    if (this.assetLink) {
      return `https://${this.assetLink}`;
    }
    return null;
  }

  renderTopSection() {
    if (this.topSectionText) {
      return <p className={styles.topSectionText}>{this.topSectionText}</p>;
    } else if (this.topSectionImage) {
      return (
        <div
          className={[this.backgroundImage ? styles.topSectionImageWrapper__full : styles.topSectionImageWrapper].join(
            " ",
          )}
        >
          <UiBkgImage
            className={[styles.topSectionImage].join(" ")}
            breakPointSmallDevice={767}
            imageUrlForSmallDevice={this.topSectionImage}
            imageUrlForLargeDevice={this.topSectionImage}
            smallDeviceImageMaxWidth={400}
            largeDeviceImageMaxWidth={900}
          />
        </div>
      );
    }
    return null;
  }

  renderButton() {
    return this.isFoundationType ? (
      <div className={styles.buttonWrapper}>
        <PrimaryButton className={["waves-effect", styles.button].join(" ")} label={this.buttonLabel} />
      </div>
    ) : null;
  }

  render() {
    if (this.backgroundImage) {
      const transform = this.state.transform;
      const wrapperStyle =
        transform && this.useParallax
          ? {
              WebkitTransform: transform,
              MozTransform: transform,
              OTransform: transform,
              msTransform: transform,
              transform: transform,
            }
          : undefined;

      return (
        <section
          className={[
            "block block--full-width block--relative block--max-width-wide",
            styles.fullWidthCard,
            borderPatterns[this.borderPattern],
            this.borderPattern ? "block--with-border" : "",
            this.componentTheme ? `component-theme component-theme-${this.componentTheme}` : "",
          ].join(" ")}
          data-contentful-id={this.props.contentful_id}
        >
          <LinkWrapper
            url={this.getLinkUrl()}
            target={!this.link && (this.linkExternalUrl || this.assetLink) ? "_blank" : undefined}
          >
            <UiBkgImage
              className={[styles.imageWrapper__full].join(" ")}
              breakPointSmallDevice={767}
              imageUrlForSmallDevice={
                this.backgroundImageSmallDevice ? this.backgroundImageSmallDevice : this.backgroundImage
              }
              imageUrlForLargeDevice={this.backgroundImage}
              smallDeviceImageMaxWidth={767}
              largeDeviceImageMaxWidth={4000}
            />
            <div
              ref={this.ref_wrapper}
              style={wrapperStyle}
              className={[
                styles.wrapper,
                styles.__overlay,
                this.alignRight ? styles.__overlay_right : styles.__overlay_left,
                borderPatterns[this.borderPattern + "_top"],
              ].join(" ")}
            >
              <div className={styles.innerWrapper}>
                {this.renderTopSection()}
                <h3
                  className={[
                    "heading-l",
                    styles.title,
                    this.isFoundationType ? styles.__foundation : styles.__overlay,
                  ].join(" ")}
                >
                  {this.title}
                </h3>
                {this.description && (
                  <p className={[styles.description].join(" ")}>
                    {StringHelper.truncateWithDots(this.description, 170)}
                  </p>
                )}
                {this.renderButton()}
              </div>
            </div>
          </LinkWrapper>
        </section>
      );
    }
    return (
      <section className="block block--relative block--max-width-wide" data-contentful-id={this.props.contentful_id}>
        <div className={styles.wrapper}>
          {this.renderTopSection()}
          <div className={styles.imageWrapper}>
            <UiBkgImage
              className={styles.image}
              breakPointSmallDevice={1023}
              imageUrlForSmallDevice={this.image}
              imageUrlForLargeDevice={this.image}
              smallDeviceImageMaxWidth={600}
              largeDeviceImageMaxWidth={900}
            />
          </div>
          <h3 className={styles.title}>{this.title}</h3>
          <p className={["font-italic", styles.description].join(" ")}>{this.description}</p>
        </div>
      </section>
    );
  }
}

export const fragmentCardListItemBlock = graphql`
  fragment CardListItemBlock on ContentfulCardListItemBlock {
    contentful_id
    id
    title
    componentTheme
    borderPattern
    alignRight
    useParallax
    link {
      slug
    }
    linkExternalUrl
    assetLink {
      openInNewTab
      asset {
        file {
          url
        }
      }
    }
    buttonLabel
    cardDescription {
      cardDescription
    }
    topSectionText
    topSectionImage {
      file {
        url
      }
    }
    image {
      file {
        url
      }
    }
    backgroundImage {
      file {
        url
      }
    }
    backgroundImageSmallDevice {
      file {
        url
      }
    }
  }
`;
